import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from '@fs/zion-router'
import { useStatusOverlay } from '@fs/zion-ui'
import useRequestState from './useRequestState'
import api from '../api/chat'
import { useBookmarks } from '../components/chat/BookmarksProvider'
import useJoinGroupByInvitation from './useJoinGroupByInvitation'
import { useChatOverlayContext } from '../components/chat/ChatOverlayProvider'
import { useChatState } from '../state/ChatState'

export default function useChatRoomInvite() {
  const [t] = useTranslation()
  const appHistory = useHistory()
  const [joinGroupChat] = useJoinGroupByInvitation()
  const { connection, user } = useChatState()
  const { setIsOpen } = useChatOverlayContext()
  const showStatusOverlay = useStatusOverlay()
  const { refetchBookmarks } = useBookmarks()

  const joinChatRoom = useCallback(
    (chatRoom, isSupportPage) => {
      showStatusOverlay({
        message: t('chat.joining', 'Joining chat room...'),
        transient: true,
      })

      return api.search
        .searchForChatRoomFromChatServer({ connection, chatRoom: chatRoom.toLowerCase() })
        .then((results) => results?.[0])
        .then((chatRoomDetails) => {
          joinGroupChat(chatRoom).then(() => {
            // Refetch bookmarks so it will properly load the friendly name
            refetchBookmarks()
            if (isSupportPage) {
              // appHistory is actually support chat overlay's history here
              appHistory.push(`/chat-room/group/${chatRoom}/${user.authzid}/`)
            } else {
              // This is just to clear the route state, so it doesn't preserve it over refreshes and cause the user to re-join the chat room
              appHistory.replace('/home', {})

              // Open the chat overlay (must be done before navigating the chat overlay)
              setIsOpen(true)

              // Navigate the chat overlay to the newly joined chat room
              window.chatHistory.push(`/chat-room/group/${chatRoom}/${user.authzid}/`)
            }
            showStatusOverlay({
              message: t('chat.joined-chat-room', 'Joined chat: "{chatRoom}"', {
                chatRoom: chatRoomDetails.friendlyName,
              }),
              type: 'success',
            })
          })
          return joinGroupChat(chatRoom)
        })
    },
    [appHistory, connection, joinGroupChat, refetchBookmarks, setIsOpen, showStatusOverlay, t, user.authzid]
  )

  return useRequestState(joinChatRoom)
}
